import { forwardRef } from 'react';
import { cn } from '../../utils/cn';
import { transitions } from '../../utils/motion';

type ToggleSize = 'sm' | 'md';

export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
  size?: ToggleSize;
  id?: string;
  className?: string;
}

export const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(
  ({ checked, onChange, label, disabled = false, size = 'md', id, className }, ref) => {
    const toggleId = id ?? (label ? `toggle-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined);

    const sizeStyles: Record<ToggleSize, { track: { width: number; height: number }; thumb: { size: number } }> = {
      sm: { track: { width: 28, height: 16 }, thumb: { size: 12 } },
      md: { track: { width: 36, height: 20 }, thumb: { size: 16 } },
    };

    const dims = sizeStyles[size];
    const trackPadding = (dims.track.height - dims.thumb.size) / 2;
    const thumbTranslate = checked ? dims.track.width - dims.thumb.size - trackPadding * 2 : 0;

    return (
      <div
        className={cn('ds-toggle', className)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <button
          ref={ref}
          id={toggleId}
          role="switch"
          aria-checked={checked}
          aria-label={label}
          disabled={disabled}
          onClick={() => {
            if (!disabled) onChange(!checked);
          }}
          onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              if (!disabled) onChange(!checked);
            }
          }}
          style={{
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            width: `${dims.track.width}px`,
            height: `${dims.track.height}px`,
            padding: `${trackPadding}px`,
            borderRadius: '999px',
            background: checked ? (disabled ? '#86a0cd' : '#002045') : disabled ? '#eceef0' : '#c4c6cf',
            border: 'none',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: transitions.fast,
            flexShrink: 0,
            outline: 'none',
          }}
          className="ds-toggle__track"
        >
          <span
            style={{
              width: `${dims.thumb.size}px`,
              height: `${dims.thumb.size}px`,
              borderRadius: '50%',
              background: disabled ? (checked ? '#adc7f7' : '#f2f4f6') : '#ffffff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              transform: `translateX(${thumbTranslate}px)`,
              transition: `transform ${transitions.fast}, background ${transitions.fast}`,
            }}
          />
        </button>

        {label && (
          <label
            htmlFor={toggleId}
            style={{
              fontSize: '0.875rem',
              color: disabled ? '#74777f' : '#191c1e',
              cursor: disabled ? 'not-allowed' : 'pointer',
              userSelect: 'none',
            }}
          >
            {label}
          </label>
        )}

        <style>{`
          .ds-toggle__track:focus-visible {
            box-shadow: 0 0 0 2px rgba(0, 32, 69, 0.3);
          }
          @media (prefers-reduced-motion: reduce) {
            .ds-toggle__track { transition: none !important; }
            .ds-toggle__track span { transition: none !important; }
          }
        `}</style>
      </div>
    );
  },
);

Toggle.displayName = 'Toggle';

